import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    PlusCircle,
    BarChart2,
    Home,
    Calendar,
    DollarSign
} from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';

const DashboardHome = () => {
  const navigate = useNavigate();
  
  return (
    <MainLayout>
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard de Vendas</h1>
          <p className="text-gray-500">Visão geral das vendas e desempenho comercial</p>
        </div>
        <div className="flex space-x-3">
          <button 
            className="bg-blue-900 text-white px-4 py-2 rounded-md flex items-center"
            onClick={() => navigate('/sales/flow')}
          >
            <PlusCircle size={18} className="mr-2" />
            Nova Venda
          </button>
          <button 
            className="border border-blue-900 text-blue-900 px-4 py-2 rounded-md flex items-center"
            onClick={() => navigate('/reports')}
          >
            <BarChart2 size={18} className="mr-2" />
            Relatórios
          </button>
        </div>
      </div>
      
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-gray-500">Vendas Realizadas</div>
          <div className="mt-2 flex items-end">
            <div className="text-3xl font-bold text-blue-900">48</div>
            <div className="ml-2 text-sm font-medium text-green-600">+12%</div>
          </div>
          <div className="text-sm text-gray-500 mb-4">vs mês anterior</div>
          <div className="h-2 bg-gray-200 rounded-full">
            <div className="h-2 bg-green-500 rounded-full w-3/4"></div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-gray-500">Receita Total</div>
          <div className="mt-2 flex items-end">
            <div className="text-3xl font-bold text-blue-900">R$ 3.2M</div>
            <div className="ml-2 text-sm font-medium text-green-600">+8%</div>
          </div>
          <div className="text-sm text-gray-500 mb-4">vs mês anterior</div>
          <div className="h-2 bg-gray-200 rounded-full">
            <div className="h-2 bg-green-500 rounded-full w-2/3"></div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-gray-500">Taxa de Conversão</div>
          <div className="mt-2 flex items-end">
            <div className="text-3xl font-bold text-blue-900">32%</div>
            <div className="ml-2 text-sm font-medium text-red-600">-3%</div>
          </div>
          <div className="text-sm text-gray-500 mb-4">vs mês anterior</div>
          <div className="h-2 bg-gray-200 rounded-full">
            <div className="h-2 bg-red-500 rounded-full w-1/3"></div>
          </div>
        </div>
      </div>
      
      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar Section - 2/3 width */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm">
          <div className="p-6">
            <h2 className="text-lg font-bold text-gray-900">Agenda de Hoje</h2>
            <div className="mt-4 space-y-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-blue-100 text-blue-900 rounded-md p-3 text-center w-20">
                  <div className="text-sm font-medium">09:30</div>
                </div>
                <div className="ml-4 flex-1 border-b border-gray-200 pb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-gray-900">Visita ao Empreendimento</h3>
                      <p className="text-gray-500">Cliente: João Silva</p>
                    </div>
                    <button 
                      className="bg-blue-50 text-blue-900 rounded-full px-3 py-1 text-xs"
                      onClick={() => navigate('/appointment-details')}
                    >
                      Detalhes
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-green-100 text-green-700 rounded-md p-3 text-center w-20">
                  <div className="text-sm font-medium">11:00</div>
                </div>
                <div className="ml-4 flex-1 border-b border-gray-200 pb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-gray-900">Assinatura de Contrato</h3>
                      <p className="text-gray-500">Cliente: Maria Oliveira</p>
                    </div>
                    <button 
                      className="bg-blue-50 text-blue-900 rounded-full px-3 py-1 text-xs"
                      onClick={() => navigate('/contract-details')}
                    >
                      Detalhes
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-yellow-100 text-yellow-700 rounded-md p-3 text-center w-20">
                  <div className="text-sm font-medium">15:30</div>
                </div>
                <div className="ml-4 flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-gray-900">Reunião com Investidor</h3>
                      <p className="text-gray-500">Cliente: Roberto Mendes</p>
                    </div>
                    <button 
                      className="bg-blue-50 text-blue-900 rounded-full px-3 py-1 text-xs"
                      onClick={() => navigate('/meeting-details')}
                    >
                      Detalhes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Availability Chart - 1/3 width */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6">
            <h2 className="text-lg font-bold text-gray-900">Disponibilidade</h2>
            <div className="flex items-center justify-center py-8">
              <div className="relative h-48 w-48">
                {/* Placeholder for actual chart */}
                <div className="h-full w-full rounded-full border-8 border-gray-100 flex items-center justify-center">
                  <div className="absolute top-0 left-0 h-full w-full">
                    <div className="absolute top-0 left-0 h-full w-full rounded-full border-8 border-transparent border-t-green-500 border-r-green-500 transform rotate-45"></div>
                    <div className="absolute top-0 left-0 h-full w-full rounded-full border-8 border-transparent border-b-yellow-500 transform rotate-45"></div>
                    <div className="absolute top-0 left-0 h-full w-full rounded-full border-8 border-transparent border-l-red-500 transform rotate-45"></div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-900">42%</div>
                    <div className="text-sm text-gray-500">Disponível</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <div className="flex items-center">
                <div className="h-3 w-3 bg-green-500 rounded-full mr-2"></div>
                <span className="text-sm text-gray-700">Disponível (42%)</span>
              </div>
              <div className="flex items-center">
                <div className="h-3 w-3 bg-yellow-500 rounded-full mr-2"></div>
                <span className="text-sm text-gray-700">Reservado (18%)</span>
              </div>
              <div className="flex items-center">
                <div className="h-3 w-3 bg-red-500 rounded-full mr-2"></div>
                <span className="text-sm text-gray-700">Vendido (25%)</span>
              </div>
              <div className="flex items-center">
                <div className="h-3 w-3 bg-gray-200 rounded-full mr-2"></div>
                <span className="text-sm text-gray-700">Bloqueado (15%)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Recent Sales Table */}
      <div className="mt-6 bg-white rounded-lg shadow-sm">
        <div className="p-6">
          <h2 className="text-lg font-bold text-gray-900">Vendas Recentes</h2>
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unidade
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">João Carlos Silva</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">Torre Sul 1004 - Sem. 14</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">R$ 58.500,00</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">22/04/2025</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      Assinado
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button 
                      className="text-blue-900 hover:text-blue-700"
                      onClick={() => navigate('/sale-details')}
                    >Ver</button>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">Maria Oliveira Santos</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">Torre Norte 505 - Completo</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">R$ 780.000,00</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">20/04/2025</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                      Pendente
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button 
                      className="text-blue-900 hover:text-blue-700"
                      onClick={() => navigate('/sale-details')}
                    >Ver</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-500">
              Mostrando 1-4 de 48 vendas
            </div>
            <div className="flex space-x-1">
              <button className="px-3 py-1 bg-blue-900 text-white rounded">1</button>
              <button className="px-3 py-1 bg-white text-gray-700 rounded border border-gray-300">2</button>
              <button className="px-3 py-1 bg-white text-gray-700 rounded border border-gray-300">3</button>
              <button className="px-3 py-1 bg-white text-gray-700 rounded border border-gray-300">▶</button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom Section: Shortcuts and Campaigns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/* Shortcuts */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6">
            <h2 className="text-lg font-bold text-gray-900">Atalhos Rápidos</h2>
            <div className="mt-4 space-y-2">
              <button 
                className="w-full bg-gray-50 hover:bg-gray-100 py-3 px-4 rounded flex items-center text-gray-700"
                onClick={() => navigate('/virtual-model')}
              >
                <Home size={18} className="mr-2 text-blue-900" />
                <span>Maquete Virtual</span>
              </button>
              <button 
                className="w-full bg-gray-50 hover:bg-gray-100 py-3 px-4 rounded flex items-center text-gray-700"
                onClick={() => navigate('/schedule')}
              >
                <Calendar size={18} className="mr-2 text-blue-900" />
                <span>Agendar Atendimento</span>
              </button>
              <button 
                className="w-full bg-gray-50 hover:bg-gray-100 py-3 px-4 rounded flex items-center text-gray-700"
                onClick={() => navigate('/sales-report')}
              >
                <BarChart2 size={18} className="mr-2 text-blue-900" />
                <span>Relatório de Vendas</span>
              </button>
              <button 
                className="w-full bg-gray-50 hover:bg-gray-100 py-3 px-4 rounded flex items-center text-gray-700"
                onClick={() => navigate('/commissions')}
              >
                <DollarSign size={18} className="mr-2 text-blue-900" />
                <span>Comissões do Mês</span>
              </button>
            </div>
          </div>
        </div>
        
        {/* Campaigns - 2/3 width */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm">
          <div className="p-6">
            <h2 className="text-lg font-bold text-gray-900">Campanhas Ativas</h2>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-blue-100 rounded-lg overflow-hidden">
                <div className="bg-blue-900 text-white p-4">
                  <h3 className="font-bold text-lg">Black Friday 2025</h3>
                </div>
                <div className="p-4">
                  <div className="mb-2"><span className="font-medium">Desconto:</span> 10%</div>
                  <div className="mb-2"><span className="font-medium">Bônus:</span> + Semana bônus</div>
                  <div className="mb-4"><span className="font-medium">Válido até:</span> 30/11/2025</div>
                  <button 
                    className="bg-blue-900 text-white px-4 py-2 rounded text-sm"
                    onClick={() => navigate('/apply-campaign')}
                  >Aplicar</button>
                </div>
              </div>
              
              <div className="border border-green-100 rounded-lg overflow-hidden">
                <div className="bg-green-600 text-white p-4">
                  <h3 className="font-bold text-lg">Primavera Especial</h3>
                </div>
                <div className="p-4">
                  <div className="mb-2"><span className="font-medium">Desconto:</span> 5%</div>
                  <div className="mb-2"><span className="font-medium">Aplicação:</span> Torre Sul exclusivo</div>
                  <div className="mb-4"><span className="font-medium">Válido até:</span> 21/12/2025</div>
                  <button 
                    className="bg-green-600 text-white px-4 py-2 rounded text-sm"
                    onClick={() => navigate('/apply-campaign')}
                  >Aplicar</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default DashboardHome;